import { Link, useLocation, useNavigate } from "react-router-dom"
import { LanguageDropdown } from './languagedropdown'
import { app, translate } from "../app"
import { Container } from "./container"
import { languages } from "../locale"
import { useEffect } from "react"

import {
  Facebook,
  Instagram,
  Whatsapp,
  Tiktok,
  Telegram,
  FacebookMessenger,
  WhatsappSquare,
  Youtube,
  XTwitter,
  Envelope
} from "./icons"
import { translations } from "../translations"
import { servicesMenuTarget } from "../helpers"

export function Footer() {

  const location = useLocation()
  const isRTL = languages[app.locale]?.rtl

  const footerMenu: Menu[] = app.page.header.menu.filter((item: Menu) => ["services"].includes(item.type))
  const channels = app.page.content.find(x => x.type === 'channels') as BlockChannels

  const links = [
    { link: channels?.fb_link, label: 'Facebook', icon: Facebook },
    { link: channels?.fbmess_link, label: 'Facebook Messenger', icon: FacebookMessenger },
    { link: channels?.whatsapp_link, label: 'WhatsApp', icon: Whatsapp },
    { link: channels?.email_link, label: 'Email', icon: Envelope, isEmail: true },
    { link: channels?.instagram_link, label: 'Instagram', icon: Instagram },
    { link: channels?.tiktok_link, label: 'Tiktok', icon: Tiktok },
    { link: channels?.telegram_link, label: 'Telegram', icon: Telegram },
    { link: channels?.whatsappc_link, label: 'WhatsApp Channel', icon: WhatsappSquare },
    { link: channels?.youtube_link, label: 'Youtube', icon: Youtube },
    { link: channels?.X_link, label: 'X', icon: XTwitter },
  ]

  useEffect(() => {
    if (location.pathname === "/service-map") {
      const targetElement = document.getElementById("service-map")
      if (targetElement) targetElement.scrollIntoView({ behavior: "smooth" })
    }
  }, [location.pathname, location.hash])

  if (app.country === 42 || app.country === 43) {
    return (
      <footer className={`footer-black-text py-4 sm:py-0 ${isRTL ? 'rtl' : ''}`} dir={isRTL ? 'rtl' : 'ltr'}>
        <Container block={app.page.footer}>
          <p dir="ltr">
            {translate(translations.ircMapDisclaimer)}
          </p>
          <p>
            {translate(translations.ircFundingDisclaimer)}
          </p>
        </Container>
      </footer>
    )
  }

  return (
    <footer className={`footer-black-text py-4 sm:py-0 ${isRTL ? 'rtl' : ''}`} dir={isRTL ? 'rtl' : 'ltr'}>
      <Container block={app.page.footer}>
        <div className="flex flex-col gap-10">
          <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 ${isRTL ? 'footer-grid-rtl' : ''}`}>
            {/* Logo and Social Icons Column */}
            <div className={`flex flex-col gap-4 ${isRTL ? 'lg:order-4' : ''}`}>
              <Link to="/">
                {app.logo && <img src={app.logo} alt="Logo" className="h-10 mb-4" loading="lazy" />}
              </Link>
              {links.length > 0 && <div className={`flex flex-wrap gap-3 items-center footer-social-icons ${isRTL ? 'flex-row-reverse' : ''}`}>
                {links.map(({ link, label, icon: Icon, isEmail }, index) => link ? (<SocialLink key={index} href={isEmail ? `mailto:${translate(link)}` : translate(link)} ariaLabel={label} Icon={Icon} style={{ fontSize: '1.4em' }} />) : null)}
              </div>}
            </div>

            {/* Categories Column */}
            {Object.values(app.data.zendesk.categories).length > 0 && <div className={isRTL ? 'lg:order-3' : ''}>
              <h3 className={`text-base font-bold mb-4 text-black ${isRTL ? 'text-right' : 'text-left'}`} style={{ direction: isRTL ? "rtl" : "ltr" }}>{translate(translations.categories)}</h3>
              <ul className="flex flex-col gap-2 list-none p-0">
                {renderCategories()}
              </ul>
            </div>}

            {/* Resources/Footer Menu Column */}
            <div className={isRTL ? 'lg:order-2' : ''}>
              <h3 className={`text-base font-bold mb-4 text-black ${isRTL ? 'text-right' : 'text-left'}`} style={{ direction: isRTL ? "rtl" : "ltr" }}>{translate(translations.resources)}</h3>
              <ul className="flex flex-col gap-2 list-none p-0">
                {renderFooterItems(footerMenu)}
              </ul>
            </div>

            {/* Links Column */}
            <div className={isRTL ? 'lg:order-1' : ''}>
              {app.page.footer?.footerlinks.length > 0 && <>
                <h3 className={`text-base font-bold mb-4 text-black ${isRTL ? 'text-right' : 'text-left'}`} style={{ direction: isRTL ? "rtl" : "ltr" }}>{translate(translations.links)}</h3>
                <ul className="flex flex-col gap-2 list-none p-0">
                  {app.page.footer?.footerlinks.map((link) => (
                    <li key={`${link.title}-${link.url}`}>
                      <Link to={link.url} className={`text-black hover:text-link-hover text-base font-normal leading-normal transition-default ${isRTL ? 'text-right' : 'text-left'}`} style={{ direction: isRTL ? "rtl" : "ltr" }}>
                        {translate(link.title)}
                      </Link>
                    </li>
                  ))}
                </ul>
              </>}
              <div className="mt-4">
                <LanguageDropdown />
              </div>
            </div>
          </div>

          {/* Copyright Row */}
          <div className="border-t border-gray-300 pt-4">
            <p className={`text-neutral-icon font-sans text-[14px] font-normal leading-[18px] !m-0 ${isRTL ? 'text-right' : 'text-left'}`} style={{ direction: isRTL ? "rtl" : "ltr" }}>
              {translate(app.page.footer.text)}
            </p>
          </div>
        </div>
      </Container>
    </footer>
  )
}
function SocialLink({ href, ariaLabel, Icon, style }) {
  return <a
    style={style}
    href={href}
    data-animation="animate__fadeInUp"
    target="_blank"
    aria-label={ariaLabel}
    rel="noopener noreferrer"
    className="text-black hover:text-link-hover no-underline text-current focus:outline-none transition-default"
  >
    <Icon aria-hidden="true" />
  </a>
}

function renderFooterItems(menuItems: Menu[]) {
  const isRTL = languages[app.locale]?.rtl
  const locale = languages[app.locale]?.zendesk as string ?? app.locale

  return menuItems.map((item) => {
    const title = item.title ? translate(item.title) : ""
    const textAlign = isRTL ? 'text-right' : 'text-left'
    if (item.type === "services") {
      return (
        <li key={title}>
          <Link to={servicesMenuTarget(locale)} className={`text-base font-normal leading-snug cursor-pointer text-black hover:text-link-hover transition-default ${textAlign}`}>
            {title}
          </Link>
        </li>
      )
    } else {
      return (
        <li key={title}>
          <Link to={item.link || "#"} className={`text-base font-normal leading-snug text-black hover:text-link-hover transition-default ${textAlign}`}>{title}</Link>
        </li>
      )
    }
  })
}

function renderCategories() {
  const isRTL = languages[app.locale]?.rtl
  const categories: { [index: number]: ZendeskCategory } = app.data.zendesk.categories
  const locale = languages[app.locale]?.zendesk as string ?? app.locale
  const textAlign = isRTL ? 'text-right' : 'text-left'
  return Object.values(categories).map((category) => (
    <li key={category.id}>
      <Link to={`/${locale.toLowerCase()}/categories/${category.id}`} className={`text-black hover:text-link-hover text-base font-normal leading-snug block transition-default ${textAlign}`}>
        {translate(category.name)}
      </Link>
    </li>
  ))
}

